import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createListNode,
  $isListItemNode,
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_LOW,
  KEY_DOWN_COMMAND,
  KEY_TAB_COMMAND,
} from 'lexical';
import { $getNearestNodeOfType } from '@lexical/utils';

import type { RangeSelection } from 'lexical';

import { Icon } from 'components/Icon';
import styles from 'components/RteEditor/RteEditor.module.css';
import { useTranslation } from 'components/RteEditor/hooks/useTranslation';

type ListTypesType = 'bullet' | 'number' | null;
const LIST_TYPE_ICONS = {
  bullet: 'BulletList',
  number: 'NumberedList',
};

const ListTypes = ['bullet', 'number'];

export const ListPlugin = () => {
  const t = useTranslation();
  const [editor] = useLexicalComposerContext();
  const [blockType, setBlockType] = useState<ListTypesType>(null);
  const [isUList, setIsUList] = useState<boolean>(false);
  const [isOList, setIsOList] = useState<boolean>(false);

  const formatList = (listType: ListTypesType): void => {
    if (listType === 'number' && blockType !== 'number') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
      setBlockType('number');
    } else if (listType === 'bullet' && blockType !== 'bullet') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
      setBlockType('bullet');
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      setBlockType(null);
    }
  };

  useEffect(() => {
    const removeKeyboardShortcut = editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        if (event.ctrlKey && event.shiftKey && event.code === 'Digit7') {
          event.preventDefault();
          if (isOList) {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
          } else {
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
          }
          return true;
        }
        if (event.ctrlKey && event.shiftKey && event.code === 'Digit8') {
          event.preventDefault();
          if (isUList) {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
          } else {
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
          }
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_CRITICAL
    );

    return () => {
      removeKeyboardShortcut();
    };
  }, [editor, isOList, isUList]);

  useEffect(() => {
    const removeTabCommand = editor.registerCommand(
      KEY_TAB_COMMAND,
      (event) => {
        event.preventDefault();
        editor.update(() => {
          const selection = $getSelection() as RangeSelection;
          if (!$isRangeSelection(selection)) {
            return;
          }

          const currentNode = selection.anchor.getNode();
          const parent = currentNode.getParent();
          if (!$isListItemNode(parent)) {
            return;
          }

          const listParent = parent.getParent();
          if ($isListNode(listParent)) {
            const newList = $createListNode($isListNode(listParent) ? listParent.getListType() : 'bullet');
            parent.append(newList);
            newList.append(currentNode);
          }
        });
        return true;
      },
      COMMAND_PRIORITY_LOW
    );

    const removeStatusListener = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const anchorNode = selection.anchor.getNode();
          const listNode = $getNearestNodeOfType(anchorNode, ListNode);
          if (listNode) {
            const tag = listNode.getTag();
            setIsUList(tag === 'ul');
            setIsOList(tag === 'ol');
            if (tag === 'ul') {
              setBlockType('bullet');
            }
            if (tag === 'ol') {
              setBlockType('number');
            }
            return;
          }
          setIsUList(false);
          setIsOList(false);
          setBlockType(null);
        }
        return false;
      });
    });

    return () => {
      removeStatusListener();
      removeTabCommand();
    };
  }, [editor]);

  return (
    <div className={styles['editor__toolbar-button-group']}>
      {ListTypes.map((listType) => (
        <button
          onClick={() => formatList(listType as ListTypesType)}
          key={listType}
          title={t.format[`${listType}Tooltip` as keyof typeof t.format]}
          className={clsx(
            styles['editor__toolbar-button'],
            styles[`editor__toolbar-button--${listType}`],
            listType === 'bullet' && isUList && styles['editor__toolbar-button--active'],
            listType === 'number' && isOList && styles['editor__toolbar-button--active']
          )}
          aria-label={t.aria.labels[listType as keyof typeof t.aria.labels]}
          aria-keyshortcuts={t.aria.keyshortcuts[listType as keyof typeof t.aria.keyshortcuts]}
          type="button"
        >
          <Icon name={LIST_TYPE_ICONS[listType as keyof typeof Icon]} size={24} />
        </button>
      ))}
    </div>
  );
};
